import { CALL_API } from 'redux-api-middleware';
import { get, isEmpty, isNumber, find } from 'lodash';
import cookie from 'react-cookie';
import {
  permissions as perms,
  util as permissionUtil,
} from 'functional-permissions-lib';

import { commonHeaders } from 'helpers/api';
import { createReducer } from 'helpers/redux';
import { getConfig } from 'modules/config';
import { profileKeys } from './constants';

export const namespace = 'user';

export const FETCH_USER = 'vtn/user/FETCH_USER';
export const FETCH_USER_SUCCESS = 'vtn/user/FETCH_USER_SUCCESS';
export const FETCH_USER_FAILURE = 'vtn/user/FETCH_USER_FAILURE';

export const LOGIN = 'vtn/user/LOGIN';
export const LOGIN_SUCCESS = 'vtn/user/LOGIN_SUCCESS';
export const LOGIN_FAILURE = 'vtn/user/LOGIN_FAILURE';

export const LOGOUT = 'vtn/user/LOGOUT';
export const LOGOUT_SUCCESS = 'vtn/user/LOGOUT_SUCCESS';
export const LOGOUT_FAILURE = 'vtn/user/LOGOUT_FAILURE';

export const REFRESH_TOKEN = 'vtn/user/REFRESH_TOKEN';
export const REFRESH_TOKEN_SUCCESS = 'vtn/user/REFRESH_TOKEN_SUCCESS';
export const REFRESH_TOKEN_FAILURE = 'vtn/user/REFRESH_TOKEN_FAILURE';

export const UPDATE_PROFILE = 'vtn/user/UPDATE_PROFILE_';
export const UPDATE_PROFILE_SUCCESS = 'vtn/user/UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_FAILURE = 'vtn/user/UPDATE_PROFILE_FAILURE';

export const UPDATE_BASIC_INFO_USER = 'vtn/user/UPDATE_BASIC_INFO_USER';
export const UPDATE_BASIC_INFO_USER_SUCCESS =
  'vtn/user/UPDATE_BASIC_INFO_USER_SUCCESS';
export const UPDATE_BASIC_INFO_USER_FAILURE =
  'vtn/user/UPDATE_BASIC_INFO_USER_FAILURE';

export const FETCH_USER_APPLICATIONS = 'vtn/user/FETCH_USER_APPLICATIONS';
export const FETCH_USER_APPLICATIONS_SUCCESS =
  'vtn/user/FETCH_USER_APPLICATIONS_SUCCESS';
export const FETCH_USER_APPLICATIONS_FAILURE =
  'vtn/user/FETCH_USER_APPLICATIONS_FAILURE';

export const LIST_MEDIA_ASSETS = 'vtn/user/LIST_MEDIA_ASSETS';
export const LIST_MEDIA_ASSETS_SUCCESS = 'vtn/user/LIST_MEDIA_ASSETS_SUCCESS';
export const LIST_MEDIA_ASSETS_FAILURE = 'vtn/user/LIST_MEDIA_ASSETS_FAILURE';

export const SET_REFERRER = 'vtn/user/SET_REFERRER';

const defaultState = {
  user: {},

  isFetching: false,
  fetchingFailed: false,

  isLoggingIn: false,
  loginFailed: false,
  loginFailureMessage: null,

  isFetchingApplications: false,
  fetchApplicationsFailed: false,
  fetchApplicationsFailureMessage: null,
  enabledApps: [],
  mediaAssets: [],
  referrer: '',
};

const reducer = createReducer(defaultState, {
  [FETCH_USER](state, action) {
    const requestSuccessState = {
      ...state,
      isFetching: true,
      fetchingFailed: false,
    };

    return action.error
      ? // handle requestError ie. offline
        this[FETCH_USER_FAILURE](state, action)
      : requestSuccessState;
  },

  [FETCH_USER_SUCCESS](state, action) {
    return {
      ...state,
      isFetching: false,
      fetchingFailed: false,
      user: action.payload,
    };
  },

  [FETCH_USER_FAILURE](state, action) {
    return {
      ...state,
      isFetching: false,
      fetchingFailed: true,
      user: {},
    };
  },

  [LOGIN](state, action) {
    const requestSuccessState = {
      ...state,
      isLoggingIn: true,
      loginFailed: false,
      loginFailureMessage: null,
    };

    return action.error
      ? this[LOGIN_FAILURE](state, action)
      : requestSuccessState;
  },

  [LOGIN_SUCCESS](state, action) {
    return {
      ...state,
      isLoggingIn: false,
      loginFailed: false,
      user: action.payload,
      loginFailureMessage: null,
    };
  },

  [LOGIN_FAILURE](state, action) {
    const statusErrors = {
      404: "Couldn't login, please double check your username and password.",
      default: "Couldn't login, please try again.",
    };

    const failureMessage =
      action.payload.name === 'ApiError'
        ? statusErrors[action.payload.status] || statusErrors.default
        : action.payload.name === 'RequestError'
        ? 'There was an error while logging in, please try again.'
        : statusErrors.default;

    return {
      ...state,
      isLoggingIn: false,
      loginFailed: true,
      loginFailureMessage: failureMessage,
      user: {},
    };
  },

  [FETCH_USER_APPLICATIONS](state, action) {
    const requestSuccessState = {
      ...state,
      isFetchingApplications: true,
      fetchApplicationsFailed: false,
      fetchApplicationsFailureMessage: null,
      enabledApps: [],
    };

    return action.error
      ? this[FETCH_USER_APPLICATIONS_FAILURE](state, action)
      : requestSuccessState;
  },

  [FETCH_USER_APPLICATIONS_SUCCESS](state, action) {
    return {
      ...state,
      isFetchingApplications: false,
      fetchApplicationsFailed: false,
      enabledApps: action.payload.results,
      fetchApplicationsFailureMessage: null,
    };
  },

  [FETCH_USER_APPLICATIONS_FAILURE](state, action) {
    const statusErrors = {
      404: "Couldn't get application list.",
      default: "Couldn't get application list. Please login.",
    };

    const failureMessage =
      action.payload.name === 'ApiError'
        ? statusErrors[action.payload.status] || statusErrors.default
        : action.payload.name === 'RequestError'
        ? 'There was an error when fetching application list, please try again.'
        : statusErrors.default;

    return {
      ...state,
      isFetchingApplications: false,
      fetchApplicationsFailed: true,
      fetchApplicationsFailureMessage: failureMessage,
      enabledApps: [],
    };
  },

  [UPDATE_PROFILE_SUCCESS](state, action) {
    return {
      ...state,
      user: action.payload,
    };
  },

  [UPDATE_BASIC_INFO_USER_SUCCESS](state, action) {
    return {
      ...state,
      user: {
        ...state.user,
        ...action.payload,
      },
    };
  },

  [REFRESH_TOKEN_SUCCESS](state, action) {
    return {
      ...state,
      user: action.payload,
    };
  },
  [LIST_MEDIA_ASSETS_SUCCESS](state, action) {
    if (action.payload.errors) {
      return this[LIST_MEDIA_ASSETS_FAILURE](state, action);
    }

    const records = action.payload.data.temporalDataObjects.records;
    const mediaAssets = records
      .slice()
      .reverse()
      .map((record) => {
        const firstFileWithName = find(
          record.metadata,
          (obj) => !!obj.fileName
        );
        return {
          id: record.id,
          fileName: get(firstFileWithName, 'fileName', record.id),
        };
      });

    return {
      ...state,
      mediaAssets,
    };
  },
  [LIST_MEDIA_ASSETS_FAILURE](state, action) {
    return state;
  },
  [SET_REFERRER](state, action) {
    return {
      ...state,
      referrer: action.payload.referrer,
    };
  },
});

export default reducer;

function local(state) {
  return state[namespace];
}

export function fetchUser() {
  return {
    [CALL_API]: {
      types: [FETCH_USER, FETCH_USER_SUCCESS, FETCH_USER_FAILURE],
      endpoint: (state) => `${getConfig(state).restApiRoot}/admin/current-user`,
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    },
  };
}

export function login({ userName, password }) {
  return {
    [CALL_API]: {
      types: [LOGIN, LOGIN_SUCCESS, LOGIN_FAILURE],
      endpoint: (state) => `${getConfig(state).restApiRoot}/admin/login`,
      method: 'POST',
      body: JSON.stringify({
        userName,
        password,
      }),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    },
  };
}

export function logout() {
  return {
    [CALL_API]: {
      types: [LOGOUT, LOGOUT_SUCCESS, LOGOUT_FAILURE],
      endpoint: (state) =>
        // prettier-ignore
        `${getConfig(state).restApiRoot}/admin/token/${selectSessionToken(state)}/logout`,
      method: 'GET',
      headers: commonHeaders,
    },
  };
}

export function refreshApiToken() {
  return {
    [CALL_API]: {
      types: [REFRESH_TOKEN, REFRESH_TOKEN_SUCCESS, REFRESH_TOKEN_FAILURE],
      endpoint: (state) =>
        // prettier-ignore
        `${getConfig(state).restApiRoot}/admin/token/${selectSessionToken(state)}/refresh`,
      method: 'GET',
      headers: commonHeaders,
    },
  };
}

export function setBasicUserProfile(values) {
  const input = {
    firstName: values.firstName,
    lastName: values.lastName,
    /* Remove developerType and title until API supports */
    // title: values.title,
    // developerType: values.developerType
  };

  const query = `
  ${userSchemaFragment}
    mutation UpdateCurrentUser($input: UpdateCurrentUser!) {
      updateCurrentUser(input: $input) {
        ...userFields
      }
    }
  `;

  return {
    [CALL_API]: {
      types: [
        UPDATE_BASIC_INFO_USER,
        UPDATE_BASIC_INFO_USER_SUCCESS,
        UPDATE_BASIC_INFO_USER_FAILURE,
      ],
      endpoint: (state) => getConfig(state).services.graphqlUri,
      method: 'POST',
      body: JSON.stringify({
        query,
        operationName: 'UpdateCurrentUser',
        variables: {
          input: input,
        },
      }),
      headers: commonHeaders,
    },
  };
}

export function setOnboardingProfileFields(values) {
  return {
    [CALL_API]: {
      types: [UPDATE_PROFILE, UPDATE_PROFILE_SUCCESS, UPDATE_PROFILE_FAILURE],
      endpoint: (state) =>
        // prettier-ignore
        `${getConfig(state).services.coreAdminUri}/current-user/set-developer-organization-fields`,
      // endpoint:`http:local.veritone.com:9000/api/admin/current-user/set-developer-organization-fields`,
      method: 'POST',
      body: JSON.stringify(values),
      headers: commonHeaders,
    },
  };
}

export function fetchEnabledApps() {
  return {
    [CALL_API]: {
      types: [
        FETCH_USER_APPLICATIONS,
        FETCH_USER_APPLICATIONS_SUCCESS,
        FETCH_USER_APPLICATIONS_FAILURE,
      ],
      endpoint: (state) =>
        `${getConfig(state).restApiRoot}/admin/current-user/applications`,
      method: 'GET',
      headers: commonHeaders,
      credentials: 'include',
    },
  };
}

export function listMediaAssets() {
  const query = `
    query {
      temporalDataObjects(sampleMedia: true) {
        records {
          id
          metadata {
            ... on FileData {
              fileName
            }
          }
        }
      }
    }`;
  return {
    [CALL_API]: {
      types: [
        LIST_MEDIA_ASSETS,
        LIST_MEDIA_ASSETS_SUCCESS,
        LIST_MEDIA_ASSETS_FAILURE,
      ],
      endpoint: (state) => getConfig(state).services.graphqlUri,
      method: 'POST',
      body: JSON.stringify({
        query,
      }),
      headers: commonHeaders,
    },
  };
}

export function setReferrer(referrer) {
  return {
    type: SET_REFERRER,
    payload: {
      referrer,
    },
  };
}

export function isLoggingIn(state) {
  return local(state).isLoggingIn;
}

export function loginFailed(state) {
  return local(state).loginFailed;
}

export function loginFailureMessage(state) {
  return local(state).loginFailureMessage;
}

export function selectUser(state) {
  return local(state).user;
}

export function selectUserOrganizationKvp(state) {
  return get(local(state).user, 'organization.kvp');
}

export function selectSessionToken(state) {
  return get(local(state), 'user.token');
}

export function enabledAppsFailedLoading(state) {
  return local(state).fetchApplicationsFailed;
}

export function isFetchingApps(state) {
  return local(state).isFetchingApplications;
}

export function enabledAppsFailureMessage(state) {
  return local(state).fetchApplicationsFailureMessage;
}

export function selectEnabledApps(state) {
  let migrated = cookie.load('veritone-migrated-to-discovery') === 'true';
  let appList = local(state).enabledApps;

  return appList
    .map((app) => {
      let alteredApp = { ...app };

      if (
        app.applicationCheckPermissions &&
        perms[app.applicationKey] &&
        isNumber(perms[app.applicationKey].access)
      ) {
        alteredApp.permissionId = perms[app.applicationKey].access;
      }

      if (app.applicationKey === 'advertiser') {
        alteredApp.applicationName = migrated ? 'Discovery' : 'Advertiser';
      } else if (app.applicationKey === 'broadcaster') {
        alteredApp.applicationName = migrated ? 'Discovery' : 'Media';
      }

      return alteredApp;
    })
    .filter((app) => {
      return (
        (app.permissionId &&
          permissionUtil.hasAccessTo(
            app.permissionId,
            selectUser(state).permissionMasks
          )) ||
        !app.applicationCheckPermissions
      );
    });
}

export function userIsAuthenticated(state) {
  return !isEmpty(local(state).user);
}

export function userCompletedDevSignup(localState) {
  return get(localState, 'user.organization.developerFieldsCompleted', false);
}

export function getMediaAssets(state) {
  return local(state).mediaAssets;
}

export function getReferrer(state) {
  return local(state).referrer;
}

export function selectProgressProfile(state) {
  const userInfo = selectUserOrganizationKvp(state);
  let filledValues = profileKeys.filter((key) => !!userInfo[key]);
  let profileKeysLength = profileKeys.length;

  if (
    !userInfo.earlyAdopterProgram ||
    userInfo.earlyAdopterProgram === 'None'
  ) {
    filledValues = filledValues.filter((key) => {
      if (key === 'isReferred' || key === 'referredBy' || key === 'useCase') {
        return false;
      }
      return true;
    });
    profileKeysLength -= 3; // 3 represents the fields in the if statement
  } else {
    if (!filledValues.includes('isReferred')) {
      filledValues = filledValues.filter((key) => key !== 'referredBy');
      profileKeysLength -= 2; // 2 represents the isReferred field and the referredBy field
    }
  }

  const progressValue = Math.round(
    100 * (filledValues.length / profileKeysLength)
  );
  return progressValue;
}

// TODO: readd developerType
const userSchemaFragment = `
  fragment userFields on User {
    firstName
    lastName
  }
`;



// WEBPACK FOOTER //
// ./src/client/modules/user/index.js